import type { FailCodeEntity, FailCodePartEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

/**
 * Parameters for searching part numbers by keyword query.
 */
export type SearchPartNumbersParams = {
    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** The search query string to match against part numbers. */
    query?: string;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;
};

/**
 * The result of a part number search, containing both matching fail codes and parts.
 */
export type SearchPartNumbersResult = {
    /** The list of fail codes matching the part number search query. */
    failCodes: FailCodeEntity[];

    /** The list of parts matching the search query. */
    parts: FailCodePartEntity[];
};

/**
 * Search for part numbers by query text, scoped to a specific vehicle configuration.
 * Used by the search-by-part flow in the fail codes modal.
 */
export class SearchPartNumbers extends StsBackendOperation {
    /**
     * Search part numbers from the API and return them as domain entities.
     *
     * @param params - the vehicle identifiers, search query, and optional abort signal
     * @returns the matching fail codes and parts as domain entities
     */
    async exec(params: SearchPartNumbersParams): Promise<SearchPartNumbersResult> {
        const { signal, ...request } = params;
        const response = await this.client.searchPartNumbers(request, signal);

        if (!response.success) {
            return { failCodes: [], parts: [] };
        }

        return {
            failCodes: response.data.failCodes ?? [],
            parts: response.data.parts ?? [],
        };
    }
}
